"use client";

import {
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react";
import {
  getAreaAspectRatio,
  getCropAreaAspectRatio,
  getCropAreaSize,
  getFaceWindow,
} from "@/lib/print";
import {
  getCropGeometry,
  getCropStateForWindow,
  resizeCropWindow,
  type CropCorner,
  type CropWindow,
  type ImageCrop,
} from "@/lib/image-crop";
import type { GalleryImage, ImageArea } from "@/lib/types";

type DragState = {
  pointerId: number;
  mode: "move" | "resize";
  corner?: CropCorner;
  startX: number;
  startY: number;
  initialCrop: Pick<ImageCrop, "x" | "y" | "zoom">;
  initialWindow: CropWindow;
  width: number;
  height: number;
};

type ImageAreaEditorProps = {
  area: ImageArea;
  src: string | null;
  crop: ImageCrop | null;
  onCropChange: (updates: Partial<ImageCrop>) => void;
  onSelectFile: (file: File) => void;
  onImageError: () => void;
  showPreview?: boolean;
  galleryImages?: GalleryImage[];
};

export default function ImageAreaEditor({
  area,
  src,
  crop,
  onCropChange,
  onSelectFile,
  onImageError,
  showPreview = true,
  galleryImages = [],
}: ImageAreaEditorProps) {
  const drag = useRef<DragState | null>(null);
  const uploadInput = useRef<HTMLInputElement>(null);
  const galleryDialog = useRef<HTMLDialogElement>(null);
  const [gallerySearch, setGallerySearch] = useState("");
  const [galleryLoadingId, setGalleryLoadingId] = useState("");
  const [galleryError, setGalleryError] = useState("");
  const hasImageDimensions = Boolean(
    crop?.sourceWidth && crop.sourceHeight,
  );
  const cropWindow = hasImageDimensions && crop
    ? getCropGeometry(area, crop, crop.sourceWidth, crop.sourceHeight).window
    : null;
  const faceWindow = cropWindow ? getFaceWindow(area, cropWindow) : null;
  const cropArea = getCropAreaSize(area);
  const faceGuide = area.wrapAllowanceCm > 0
    ? {
        left: `${(area.wrapAllowanceCm / cropArea.widthCm) * 100}%`,
        top: `${(area.wrapAllowanceCm / cropArea.heightCm) * 100}%`,
        width: `${(area.widthCm / cropArea.widthCm) * 100}%`,
        height: `${(area.heightCm / cropArea.heightCm) * 100}%`,
      }
    : null;
  const filteredGalleryImages = galleryImages.filter((image) =>
      image.fileName.toLocaleLowerCase("es-MX").includes(
        gallerySearch.trim().toLocaleLowerCase("es-MX"),
      ),
  );

  const openGallery = () => {
      setGallerySearch("");
      setGalleryError("");
      galleryDialog.current?.showModal();
  };

  const selectGalleryImage = async (image: GalleryImage) => {
      setGalleryLoadingId(image.id);
      setGalleryError("");
      try {
        const response = await fetch(image.url);
        if (!response.ok) {
          throw new Error("The gallery image request failed.");
        }
        const blob = await response.blob();
        const file = new File([blob], image.fileName, {
          type: image.mimeType,
        });
        onSelectFile(file);
        galleryDialog.current?.close();
      } catch (error) {
        console.error("Failed to load a curated gallery image.", error);
        setGalleryError("No se pudo cargar esta imagen. Inténtalo de nuevo.");
      } finally {
        setGalleryLoadingId("");
      }
  };

  const startDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (!crop || !hasImageDimensions) {
      return;
    }
    const bounds = event.currentTarget.getBoundingClientRect();
    const target = event.target instanceof Element ? event.target : null;
    const handle = target?.closest<HTMLElement>("[data-crop-handle]");
    const selection = target?.closest(".crop-selection");
    if (!handle && !selection) {
      return;
    }
    const geometry = getCropGeometry(
      area,
      crop,
      crop.sourceWidth,
      crop.sourceHeight,
    );
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = {
      pointerId: event.pointerId,
      mode: handle ? "resize" : "move",
      corner: handle?.dataset.cropHandle as CropCorner | undefined,
      startX: event.clientX,
      startY: event.clientY,
      initialCrop: { x: crop.x, y: crop.y, zoom: crop.zoom },
      initialWindow: geometry.window,
      width: bounds.width,
      height: bounds.height,
    };
  };

  const moveDrag = (event: PointerEvent<HTMLDivElement>) => {
    const activeDrag = drag.current;
    if (!activeDrag || activeDrag.pointerId !== event.pointerId || !crop) {
      return;
    }
    const deltaX = event.clientX - activeDrag.startX;
    const deltaY = event.clientY - activeDrag.startY;
    let window = activeDrag.initialWindow;
    if (activeDrag.mode === "move") {
      window = {
        ...window,
        left: Math.min(
          1 - window.width,
          Math.max(0, window.left + deltaX / activeDrag.width),
        ),
        top: Math.min(
          1 - window.height,
          Math.max(0, window.top + deltaY / activeDrag.height),
        ),
      };
    } else if (activeDrag.corner) {
      window = resizeCropWindow(
        window,
        activeDrag.corner,
        deltaX,
        deltaY,
        activeDrag.width,
        activeDrag.height,
        getCropAreaAspectRatio(area),
      );
    }
    onCropChange({
      ...activeDrag.initialCrop,
      ...getCropStateForWindow(
        area,
        { ...crop, ...activeDrag.initialCrop },
        window,
      ),
    });
  };

  const resizeWithKeyboard = (
    corner: CropCorner,
    event: KeyboardEvent<HTMLButtonElement>,
  ) => {
    const keyDeltas: Record<string, [number, number]> = {
      ArrowUp: [0, -8],
      ArrowDown: [0, 8],
      ArrowLeft: [-8, 0],
      ArrowRight: [8, 0],
    };
    const delta = keyDeltas[event.key];
    if (!delta || !crop || !hasImageDimensions) {
      return;
    }
    const stage = event.currentTarget.closest(".crop-editor");
    const bounds = stage?.getBoundingClientRect();
    if (!bounds) {
      return;
    }
    event.preventDefault();
    const window = getCropGeometry(
      area,
      crop,
      crop.sourceWidth,
      crop.sourceHeight,
    ).window;
    onCropChange(
      getCropStateForWindow(
        area,
        crop,
        resizeCropWindow(
          window,
          corner,
          delta[0],
          delta[1],
          bounds.width,
          bounds.height,
          getCropAreaAspectRatio(area),
        ),
      ),
    );
  };

  const stopDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (
      drag.current?.pointerId === event.pointerId &&
      event.currentTarget.hasPointerCapture(event.pointerId)
    ) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    drag.current = null;
  };

  const imageStyle = faceWindow
    ? {
        left: `${(-faceWindow.left / faceWindow.width) * 100}%`,
        top: `${(-faceWindow.top / faceWindow.height) * 100}%`,
        width: `${100 / faceWindow.width}%`,
        height: `${100 / faceWindow.height}%`,
      }
    : undefined;

  return (
    <>
      <div className={`image-area-editor${showPreview ? " image-area-editor-with-preview" : ""}`}>
        <div
          className="crop-editor"
          style={{
            aspectRatio: hasImageDimensions
              ? `${crop!.sourceWidth} / ${crop!.sourceHeight}`
              : String(getCropAreaAspectRatio(area)),
            maxWidth: hasImageDimensions
              ? `${(crop!.sourceWidth / crop!.sourceHeight) * 325}px`
              : undefined,
          }}
          onPointerDown={startDrag}
          onPointerMove={moveDrag}
          onPointerUp={stopDrag}
          onPointerCancel={stopDrag}
          role="group"
          aria-label={
            area.wrapAllowanceCm > 0
              ? `Ajusta el encuadre de ${area.label}; el marco interior indica la cara visible`
              : `Ajusta el encuadre de ${area.label}`
          }
        >
          {src ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={src}
              alt={`Imagen original para ${area.label}`}
              draggable={false}
              onLoad={(event) => {
                if (
                  crop &&
                  (crop.sourceWidth !== event.currentTarget.naturalWidth ||
                    crop.sourceHeight !== event.currentTarget.naturalHeight)
                ) {
                  onCropChange({
                    sourceWidth: event.currentTarget.naturalWidth,
                    sourceHeight: event.currentTarget.naturalHeight,
                  });
                }
              }}
              onError={onImageError}
            />
          ) : (
            <div className="crop-placeholder">
              <span className="placeholder-star">✳</span>
              <span>Tu diseño<br />va aquí</span>
            </div>
          )}
          {cropWindow && (
            <div
              className={`crop-selection crop-selection-${area.shape}`}
              style={{
                left: `${cropWindow.left * 100}%`,
                top: `${cropWindow.top * 100}%`,
                width: `${cropWindow.width * 100}%`,
                height: `${cropWindow.height * 100}%`,
              }}
            >
              {faceGuide && (
                <div
                  aria-hidden="true"
                  className={`crop-face-guide crop-face-guide-${area.shape}`}
                  style={faceGuide}
                />
              )}
              {(
                [
                  "top-left",
                  "top-right",
                  "bottom-left",
                  "bottom-right",
                ] as const
              ).map((corner) => (
                <button
                  className={`crop-handle crop-handle-${corner}`}
                  data-crop-handle={corner}
                  key={corner}
                  type="button"
                  aria-label={`Cambiar tamaño del encuadre, esquina ${corner.replace("-", " ")}`}
                  onKeyDown={(event) => resizeWithKeyboard(corner, event)}
                />
              ))}
            </div>
          )}
        </div>
        {showPreview && (
          <figure className="image-area-editor-preview">
            <figcaption>Vista previa</figcaption>
            <div
              className={`preview-page preview-page-${area.shape}${faceWindow ? " preview-page-with-artwork" : ""}`}
              style={{ aspectRatio: String(getAreaAspectRatio(area)) }}
            >
              {src && faceWindow ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={src} alt="" draggable={false} style={imageStyle} />
              ) : (
                <span className="preview-monogram">✳</span>
              )}
              <span className="preview-label">{area.label}</span>
            </div>
          </figure>
        )}
      </div>
      <p className="crop-help">
        {area.wrapAllowanceCm > 0
          ? `Cara visible: ${area.widthCm} × ${area.heightCm} cm. `
          : `Área de impresión: ${area.widthCm} × ${area.heightCm} cm. `}
        {crop
          ? area.wrapAllowanceCm > 0
            ? "Arrastra o cambia el tamaño del marco exterior; el interior indica lo que quedará visible."
            : "Arrastra el recuadro para moverlo; usa las esquinas para cambiar su tamaño."
          : "El recuadro mostrará el área que aparecerá en el producto."}
      </p>
      <div className="upload-control">
        <input
          ref={uploadInput}
          className="upload-input"
          hidden
          type="file"
          accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            event.currentTarget.value = "";
            if (file) {
              onSelectFile(file);
            }
          }}
        />
        <button
          className="upload-button"
          type="button"
          onClick={() => uploadInput.current?.click()}
        >
          <span>{crop ? "Cambiar imagen" : "Subir imagen"}</span>
          <span aria-hidden="true">↗</span>
        </button>
        {galleryImages.length > 0 && (
          <button
            className="upload-button gallery-picker-button"
            type="button"
            onClick={openGallery}
          >
            <span>Elegir de la galería ({galleryImages.length})</span>
            <span aria-hidden="true">▦</span>
          </button>
        )}
      </div>
      {galleryImages.length > 0 && (
        <dialog
          ref={galleryDialog}
          className="image-gallery-dialog"
          aria-label="Elegir una imagen de la galería"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              galleryDialog.current?.close();
            }
          }}
        >
          <div className="image-gallery-dialog-content">
            <header className="image-gallery-dialog-header">
              <div>
                <span className="eyebrow">IMÁGENES DE EN PAPEL</span>
                <h2>Elige una imagen</h2>
                <p>Después podrás ajustar el encuadre como con una imagen propia.</p>
              </div>
              <button
                className="icon-button"
                type="button"
                aria-label="Cerrar galería"
                onClick={() => galleryDialog.current?.close()}
              >
                ×
              </button>
            </header>
            <label className="image-gallery-search">
              <span>Buscar por nombre de archivo</span>
              <input
                type="search"
                value={gallerySearch}
                onChange={(event) => setGallerySearch(event.currentTarget.value)}
                placeholder="Ej. flores, paisaje…"
                autoFocus
              />
            </label>
            {galleryError && (
              <p className="image-gallery-error" role="alert">{galleryError}</p>
            )}
            {filteredGalleryImages.length > 0 ? (
              <div className="image-gallery-picker-grid">
                {filteredGalleryImages.map((image) => (
                  <button
                    className="image-gallery-picker-card"
                    type="button"
                    key={image.id}
                    disabled={Boolean(galleryLoadingId)}
                    onClick={() => void selectGalleryImage(image)}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={image.url} alt="" loading="lazy" />
                    <strong title={image.fileName}>{image.fileName}</strong>
                    <span>{image.width} × {image.height} px</span>
                    {galleryLoadingId === image.id && (
                      <span className="image-gallery-loading">Cargando…</span>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <p className="image-gallery-no-results">
                No hay imágenes que coincidan con esa búsqueda.
              </p>
            )}
          </div>
        </dialog>
      )}
    </>
  );
}
